import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { WalteroCard, WalteroTypography } from '../waltero';
import ExportButton from '../ExportButton';
import SensorImageThumbnail from './SensorImageThumbnail';
import { exportTableData } from '../../utils/csvExport';
import { useReportData } from '../../context/ReportDataContext';
import { 
  formatDateTime, 
  formatMeterValue, 
  formatBatteryVoltage, 
  formatRSSI, 
  formatTemperature, 
  formatConfigValue 
} from '../../utils/reportFormatters';
import { sortTableData, getSortIcon, getSortAriaSort, handleSortKeyDown } from '../../utils/reportSorting';

const SensorSummary = ({ onImageClick, onConfigHover }) => {
  const { sensorSummaryData } = useReportData();
  const [sortColumn, setSortColumn] = useState('serialNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleExportSensorSummary = useCallback(() => {
    const sortedSensors = sortTableData(sensorSummaryData, sortColumn, sortDirection);
    
    // Format sensor data for export
    const sensorData = sortedSensors.map(sensor => ({
      serialNumber: sensor.serialNumber,
      meterName: sensor.meterName,
      lastConnected: formatDateTime(sensor.lastConnected),
      lastMeterValue: formatMeterValue(sensor.lastMeterValue),
      lastBatteryVoltage: formatBatteryVoltage(sensor.lastBatteryVoltage),
      lastRSSI: formatRSSI(sensor.lastRSSI),
      lastTemperature: formatTemperature(sensor.lastTemperature)
    }));

    exportTableData('sensor-summary', { sensorData });
  }, [sensorSummaryData, sortColumn, sortDirection]);

  const sortedSensors = sortTableData(sensorSummaryData, sortColumn, sortDirection);

  if (sensorSummaryData.length === 0) {
    return (
      <div className="sensor-summary-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Sensor Summary</h3>
          <ExportButton 
            onClick={handleExportSensorSummary}
            disabled={true}
            tooltip="Export sensor summary data to CSV"
          />
        </div>
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="secondary">
            No sensor summary data available. Data will load automatically when you select an organization and date range.
          </WalteroTypography>
        </WalteroCard>
      </div>
    );
  }

  return (
    <div className="sensor-summary-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3>Sensor Summary</h3>
        <ExportButton 
          onClick={handleExportSensorSummary}
          disabled={sensorSummaryData.length === 0}
          tooltip="Export sensor summary data to CSV"
        />
      </div>
      
      <div className="sensor-summary-table">
        <table className="report-table">
          <thead>
            <tr>
              <th 
                onClick={() => handleSort('serialNumber')}
                onKeyDown={(e) => handleSortKeyDown(e, 'serialNumber', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('serialNumber', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Serial Number {getSortIcon('serialNumber', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('meterName')}
                onKeyDown={(e) => handleSortKeyDown(e, 'meterName', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('meterName', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Meter Name {getSortIcon('meterName', sortColumn, sortDirection)}
              </th>
              <th role="columnheader">Latest Image</th>
              <th 
                onClick={() => handleSort('lastConnected')}
                onKeyDown={(e) => handleSortKeyDown(e, 'lastConnected', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('lastConnected', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Last Connected {getSortIcon('lastConnected', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('lastMeterValue')}
                onKeyDown={(e) => handleSortKeyDown(e, 'lastMeterValue', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('lastMeterValue', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Last Meter Value {getSortIcon('lastMeterValue', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('lastBatteryVoltage')}
                onKeyDown={(e) => handleSortKeyDown(e, 'lastBatteryVoltage', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('lastBatteryVoltage', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Battery Voltage (V) {getSortIcon('lastBatteryVoltage', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('lastRSSI')}
                onKeyDown={(e) => handleSortKeyDown(e, 'lastRSSI', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('lastRSSI', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Last RSSI {getSortIcon('lastRSSI', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('lastTemperature')}
                onKeyDown={(e) => handleSortKeyDown(e, 'lastTemperature', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('lastTemperature', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Temperature (°C) {getSortIcon('lastTemperature', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('configInterval.value')}
                onKeyDown={(e) => handleSortKeyDown(e, 'configInterval.value', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('configInterval.value', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Interval (s) {getSortIcon('configInterval.value', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('configSendInterval.value')}
                onKeyDown={(e) => handleSortKeyDown(e, 'configSendInterval.value', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('configSendInterval.value', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Send Interval {getSortIcon('configSendInterval.value', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('configWakeOnTap.value')}
                onKeyDown={(e) => handleSortKeyDown(e, 'configWakeOnTap.value', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('configWakeOnTap.value', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Wake on Tap {getSortIcon('configWakeOnTap.value', sortColumn, sortDirection)}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSensors.map((sensor) => (
              <tr key={sensor.deviceId}>
                <td>{sensor.serialNumber}</td>
                <td>{sensor.meterName}</td>
                <td>
                  <SensorImageThumbnail 
                    deviceId={sensor.deviceId} 
                    onImageClick={onImageClick}
                  />
                </td>
                <td>{formatDateTime(sensor.lastConnected)}</td>
                <td>{formatMeterValue(sensor.lastMeterValue)}</td>
                <td>{formatBatteryVoltage(sensor.lastBatteryVoltage)}</td>
                <td>{formatRSSI(sensor.lastRSSI)}</td>
                <td>{formatTemperature(sensor.lastTemperature)}</td>
                <td 
                  className={`config-cell ${sensor.configInterval && !sensor.configInterval.synced ? 'config-out-of-sync' : ''}`}
                  onMouseEnter={(e) => onConfigHover(e, sensor.configInterval, 'Interval')}
                  onMouseLeave={() => onConfigHover(null)}
                >
                  {formatConfigValue(sensor.configInterval)}
                </td>
                <td 
                  className={`config-cell ${sensor.configSendInterval && !sensor.configSendInterval.synced ? 'config-out-of-sync' : ''}`}
                  onMouseEnter={(e) => onConfigHover(e, sensor.configSendInterval, 'Send Interval')}
                  onMouseLeave={() => onConfigHover(null)}
                >
                  {formatConfigValue(sensor.configSendInterval)}
                </td>
                <td 
                  className={`config-cell ${sensor.configWakeOnTap && !sensor.configWakeOnTap.synced ? 'config-out-of-sync' : ''}`}
                  onMouseEnter={(e) => onConfigHover(e, sensor.configWakeOnTap, 'Wake on Tap')}
                  onMouseLeave={() => onConfigHover(null)}
                >
                  {formatConfigValue(sensor.configWakeOnTap)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

SensorSummary.propTypes = {
  onImageClick: PropTypes.func.isRequired,
  onConfigHover: PropTypes.func.isRequired
};

export default SensorSummary;